import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from './ui';
import { Icon } from './Icon';

/**
 * ضبطُ حجم الصورة وموضعِها قبل حفظها — كفيسبوك.
 *
 * تُعرض الصورةُ داخل إطارٍ بنسبة القصّ المطلوبة؛ يسحبها المستخدمُ ليعيد
 * موضعَها ويكبّرها بمنزلقٍ، ثمّ تُرسم المنطقةُ الظاهرةُ وحدَها على لوحةٍ
 * بحجم الإخراج فتُحفظ مقصوصةً كما رآها. لا مكتبة: `canvas` وأحداثُ المؤشّر.
 */
export function ImageCropModal({
  file,
  srcUrl,
  aspect,
  round,
  outputW,
  outputH,
  title,
  onCancel,
  onDone,
}: {
  /** صورةٌ مختارةٌ حديثًا؛ أو `srcUrl` لتعديل صورةٍ قائمة. */
  file?: File;
  srcUrl?: string;
  /** نسبةُ الإطار: العرض ÷ الارتفاع (١ للصورة المربّعة، ٤ للغلاف). */
  aspect: number;
  round?: boolean;
  outputW: number;
  outputH: number;
  title: string;
  onCancel: () => void;
  onDone: (dataUrl: string) => void;
}) {
  const vpRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [url, setUrl] = useState<string>('');
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [vp, setVp] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  /* الملفُّ الجديدُ يصير رابطًا مؤقّتًا يُطلق عند الإغلاق؛ أو تُستخدم صورةٌ قائمة. */
  useEffect(() => {
    const created = file ? URL.createObjectURL(file) : null;
    const u = created ?? srcUrl ?? '';
    setUrl(u);
    const img = new Image();
    /* لتمكين تصدير الصور البعيدة إلى لوحةٍ دون تلويثها. */
    img.crossOrigin = 'anonymous';
    img.onload = () => setNat({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = u;
    imgRef.current = img;
    return () => {
      if (created) URL.revokeObjectURL(created);
    };
  }, [file, srcUrl]);

  /* قياسُ الإطار الفعليّ (يتجاوب مع عرض النافذة). */
  useLayoutEffect(() => {
    const el = vpRef.current;
    if (!el) return;
    const measure = () => setVp({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const baseScale = nat && vp.w ? Math.max(vp.w / nat.w, vp.h / nat.h) : 1;
  const scale = baseScale * zoom;
  const dispW = nat ? nat.w * scale : 0;
  const dispH = nat ? nat.h * scale : 0;

  /* يُبقي الصورةَ مغطّيةً الإطارَ كلَّه فلا تظهر حوافُّ فارغة. */
  const clamp = (o: { x: number; y: number }) => ({
    x: Math.min(0, Math.max(vp.w - dispW, o.x)),
    y: Math.min(0, Math.max(vp.h - dispH, o.y)),
  });

  /* عند تجهّز القياسات: توسيطُ الصورة. */
  useEffect(() => {
    if (!nat || !vp.w) return;
    setOffset(clamp({ x: (vp.w - nat.w * baseScale) / 2, y: (vp.h - nat.h * baseScale) / 2 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nat, vp.w, vp.h]);

  /* عند تغيير التكبير: إبقاء المركز ثابتًا ثمّ التقييد. */
  useEffect(() => {
    setOffset((o) => clamp(o));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const onDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setOffset(
      clamp({
        x: drag.current.ox + (e.clientX - drag.current.x),
        y: drag.current.oy + (e.clientY - drag.current.y),
      }),
    );
  };
  const onUp = () => {
    drag.current = null;
  };

  const save = () => {
    const img = imgRef.current;
    if (!img || !nat || !vp.w) return;
    const sx = -offset.x / scale;
    const sy = -offset.y / scale;
    const sw = vp.w / scale;
    const sh = vp.h / scale;
    const canvas = document.createElement('canvas');
    canvas.width = outputW;
    canvas.height = outputH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outputW, outputH);
    onDone(canvas.toDataURL('image/jpeg', 0.85));
  };

  return (
    <Modal title={title} onClose={onCancel}>
      <div
        ref={vpRef}
        className={`crop-vp${round ? ' is-round' : ''}`}
        style={{ aspectRatio: String(aspect) }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        {url && (
          <img
            className="crop-img"
            src={url}
            alt=""
            draggable={false}
            style={{
              width: dispW || undefined,
              height: dispH || undefined,
              transform: `translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
        )}
        <span className="crop-grid" aria-hidden="true" />
      </div>

      <div className="crop-zoom">
        <Icon name="search" size={15} />
        <input
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          aria-label="التكبير"
        />
      </div>
      <p className="tiny mute" style={{ margin: '2px 0 0' }}>
        اسحب الصورة لضبط موضعها، والمنزلق لتكبيرها.
      </p>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button className="btn btn-primary" onClick={save} disabled={!nat}>
          حفظ
        </button>
        <button className="btn" onClick={onCancel}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}
